import { useQuery } from '@tanstack/react-query';
import { obterWorkload, type MembroWorkload } from '@/features/projetos/projetos.api';
import { cn } from '@/lib/cn';

export function RelatorioAlocacaoPage() {
  const { data = [], isPending } = useQuery({
    queryKey: ['projetos', 'workload'],
    queryFn: obterWorkload,
    refetchInterval: 60_000,
  });

  // Group tasks by project per member
  const rows = data.map((m: MembroWorkload) => {
    const projetos = new Set(m.tasks.map((t) => t.project.id));
    return {
      ...m,
      projectCount: projetos.size,
      loadPct: m.total > 0 ? Math.round(((m.total - m.done) / m.total) * 100) : 0,
    };
  }).sort((a, b) => b.overdue - a.overdue || b.total - a.total);

  return (
    <div className="mx-auto max-w-[1200px] space-y-4">
      <header className="border-b border-border pb-3">
        <h1 className="text-base font-semibold">Relatório de Alocação</h1>
        <p className="text-xs text-muted-foreground mt-0.5">Carga de trabalho por membro nos projetos ativos</p>
      </header>

      {isPending ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Carregando…</div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Nenhum membro com tarefas em projetos ativos.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-xs text-muted-foreground">
                <th className="px-4 py-2.5 text-left font-medium">Membro</th>
                <th className="px-4 py-2.5 text-right font-medium">Projetos</th>
                <th className="px-4 py-2.5 text-right font-medium">Tarefas</th>
                <th className="px-4 py-2.5 text-right font-medium">Concluídas</th>
                <th className="px-4 py-2.5 text-right font-medium text-destructive">Atrasadas</th>
                <th className="px-4 py-2.5 text-right font-medium">Críticas</th>
                <th className="px-4 py-2.5 text-left font-medium w-40">Carga pendente</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.user.id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/20 text-[11px] font-semibold text-primary">
                        {(r.user.displayName ?? r.user.name).split(' ').slice(0,2).map((w: string) => w[0]?.toUpperCase()).join('')}
                      </div>
                      <span className="font-medium">{r.user.displayName ?? r.user.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.projectCount}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.total}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-green-600">{r.done}</td>
                  <td className={cn('px-4 py-3 text-right tabular-nums font-medium', r.overdue > 0 ? 'text-destructive' : 'text-muted-foreground')}>{r.overdue}</td>
                  <td className={cn('px-4 py-3 text-right tabular-nums', r.critical > 0 ? 'text-amber-600 font-medium' : 'text-muted-foreground')}>{r.critical}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn('h-full rounded-full', r.overdue > 0 ? 'bg-destructive' : r.loadPct > 75 ? 'bg-amber-500' : 'bg-primary')}
                          style={{ width: `${Math.min(r.loadPct, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs tabular-nums text-muted-foreground w-8 text-right">{r.loadPct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Breakdown por projeto */}
      {rows.length > 0 && (
        <details className="rounded-xl border border-border overflow-hidden">
          <summary className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-medium bg-muted/20 hover:bg-muted/40 transition-colors select-none">
            Detalhe por membro e projeto
          </summary>
          <div className="divide-y divide-border">
            {rows.map((r) => {
              const projMap = new Map<string, { name: string; number: string; tasks: typeof r.tasks }>();
              for (const t of r.tasks) {
                const p = projMap.get(t.project.id) ?? { name: t.project.name, number: t.project.number, tasks: [] as typeof r.tasks };
                p.tasks.push(t);
                projMap.set(t.project.id, p);
              }
              return (
                <div key={r.user.id} className="px-4 py-3">
                  <p className="text-xs font-semibold mb-2">{r.user.displayName ?? r.user.name}</p>
                  <div className="space-y-1">
                    {[...projMap.values()].map((p) => {
                      const done = p.tasks.filter(t => t.progress === 100).length;
                      const over = p.tasks.filter(t => t.progress < 100 && t.finishAt && t.finishAt < Math.floor(Date.now()/60000)).length;
                      return (
                        <div key={p.number} className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="font-mono w-16 shrink-0">{p.number}</span>
                          <span className="flex-1 truncate">{p.name}</span>
                          <span>{done}/{p.tasks.length}</span>
                          {over > 0 && <span className="text-destructive font-medium">{over} atrasadas</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </details>
      )}
    </div>
  );
}
